/**
 * Заняття: черга вправ на основі того, що видав планувальник.
 *
 * Типи вправ підбираються під зрілість картки — нову спершу показуємо,
 * потім перевіряємо впізнаванням, а зрілу вже змушуємо відтворити з нуля.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useStore, showTranscription } from '../lib/store.ts'
import { distractors, type Phrase } from '../lib/course.ts'
import { matchAnswer, shuffle, tokenize } from '../lib/text.ts'
import { preload, unlockAudio } from '../lib/audio.ts'
import { navigate } from '../lib/router.ts'
import { Bar, NoteView, PhraseChips, RecordCompare, Speak } from '../components/ui.tsx'
import type { Grade } from '../lib/srs.ts'

type StepKind = 'intro' | 'flip' | 'choose' | 'listen' | 'build' | 'type'
interface Step { kind: StepKind; id: string }

/** Детермінований вибір вправи: та сама картка на тому самому повторенні дає ту саму вправу. */
function pickKind(id: string, reps: number, interval: number): StepKind {
  if (interval < 2) return reps % 2 === 0 ? 'choose' : 'flip'
  let h = 2166136261
  const key = `${id}:${reps}`
  for (let i = 0; i < key.length; i++) { h ^= key.charCodeAt(i); h = Math.imul(h, 16777619) }
  const variants: StepKind[] = ['flip', 'listen', 'build', 'type', 'choose', 'listen']
  return variants[(h >>> 0) % variants.length]!
}

export function SessionScreen({ mode, moduleId }: { mode: 'daily' | 'module'; moduleId?: string }) {
  const store = useStore()
  const { idx, cards, session, settings, grade, now } = store

  const initial = useMemo<Step[]>(() => {
    if (mode === 'module' && moduleId) {
      const phrases = idx.moduleById.get(moduleId)?.phrases ?? []
      return phrases.map((p) => {
        const c = cards.get(p.id)
        return { kind: c ? pickKind(p.id, c.reps, c.interval) : 'intro', id: p.id }
      })
    }
    // Денне заняття: нові вкраплюються між повтореннями, а не йдуть стіною.
    const due: Step[] = session.due.map((c) => ({ kind: pickKind(c.id, c.reps, c.interval), id: c.id }))
    const fresh: Step[] = session.fresh.map((id) => ({ kind: 'intro' as const, id }))
    const out: Step[] = []
    const gap = fresh.length ? Math.max(1, Math.ceil(due.length / fresh.length)) : 1
    let f = 0
    for (let i = 0; i < due.length; i++) {
      out.push(due[i]!)
      if (f < fresh.length && (i + 1) % gap === 0) out.push(fresh[f++]!)
    }
    while (f < fresh.length) out.push(fresh[f++]!)
    return out
    // Черга формується один раз на вході — інакше вона перебудовувалась би після кожної оцінки.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, moduleId])

  const [queue, setQueue] = useState<Step[]>(initial)
  const [pos, setPos] = useState(0)
  const [done, setDone] = useState(0)
  const startedAt = useRef(Date.now())

  useEffect(() => { unlockAudio() }, [])
  useEffect(() => {
    preload(queue.slice(pos, pos + 6).map((s) => idx.phraseById.get(s.id)?.audio))
  }, [queue, pos, idx])

  const step = queue[pos]
  const phrase = step ? idx.phraseById.get(step.id) : undefined

  const answer = (g: Grade) => {
    if (!step) return
    const ms = Date.now() - startedAt.current
    startedAt.current = Date.now()
    grade(step.id, g, ms)
    setDone((d) => d + 1)

    if (g === 'again') {
      // Забуту картку повертаємо в цю ж сесію — через кілька інших.
      setQueue((q) => {
        const next = [...q]
        const insertAt = Math.min(next.length, pos + 4)
        next.splice(insertAt, 0, { kind: 'flip', id: step.id })
        return next
      })
    }
    setPos((p) => p + 1)
  }

  /** Після показу нової фрази одразу ставимо перевірку на впізнавання. */
  const afterIntro = () => {
    if (!step) return
    setQueue((q) => {
      const next = [...q]
      next.splice(Math.min(next.length, pos + 2), 0, { kind: 'choose', id: step.id })
      return next
    })
    startedAt.current = Date.now()
    setPos((p) => p + 1)
  }

  if (!step || !phrase) return <Finished done={done} mode={mode} />

  const total = queue.length
  const showTr = showTranscription(settings, now)

  // key передається окремо: React не приймає його через spread.
  const stepKey = `${pos}-${step.id}`
  const common = { phrase, showTr, onGrade: answer }

  return (
    <div className="study">
      <div className="topbar">
        <button className="back" onClick={() => navigate('/')} aria-label="Вийти">✕</button>
        <div style={{ flex: 1 }}>
          <Bar value={pos} max={total} />
        </div>
        <span className="tiny faint" style={{ minWidth: 46, textAlign: 'right' }}>
          {pos + 1} / {total}
        </span>
      </div>

      {step.kind === 'intro' && <Intro key={stepKey} phrase={phrase} showTr={showTr} onNext={afterIntro} />}
      {step.kind === 'flip' && <Flip key={stepKey} {...common} />}
      {step.kind === 'choose' && <Choose key={stepKey} {...common} store={store} />}
      {step.kind === 'listen' && <Listen key={stepKey} {...common} store={store} />}
      {step.kind === 'build' && <Build key={stepKey} {...common} />}
      {step.kind === 'type' && <Type key={stepKey} {...common} />}
    </div>
  )
}

// ── Знайомство з новою фразою ─────────────────────────────────────────────

function Intro({ phrase, showTr, onNext }: { phrase: Phrase; showTr: boolean; onNext: () => void }) {
  return (
    <>
      <div className="study__body">
        <span className="chip">нова фраза</span>
        <div className="study__pl">{phrase.pl}</div>
        <Speak audio={phrase.audio} text={phrase.pl} auto />
        <div className="study__uk">{phrase.uk}</div>
        {showTr && phrase.tr && <div className="study__tr">{phrase.tr}</div>}
        {phrase.literal && <div className="study__literal">дослівно: {phrase.literal}</div>}
        <PhraseChips phrase={phrase} />
        {(phrase.notes ?? []).map((n, i) => <NoteView key={i} note={n} />)}
        <RecordCompare phraseId={phrase.id} audio={phrase.audio} text={phrase.pl} />
      </div>
      <div style={{ padding: 'var(--sp-3) var(--sp-4) var(--sp-4)' }}>
        <button className="btn btn--primary btn--wide" onClick={onNext}>Далі</button>
      </div>
    </>
  )
}

// ── Картка з перевертанням (самооцінка) ───────────────────────────────────

function Flip({ phrase, showTr, onGrade }: { phrase: Phrase; showTr: boolean; onGrade: (g: Grade) => void }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <div className="study__body" onClick={() => !open && setOpen(true)}>
        <span className="chip">згадай польською</span>
        <div className="study__uk" style={{ fontSize: 24, color: 'var(--text)' }}>{phrase.uk}</div>
        {open ? (
          <>
            <div className="study__pl">{phrase.pl}</div>
            <Speak audio={phrase.audio} text={phrase.pl} auto />
            {showTr && phrase.tr && <div className="study__tr">{phrase.tr}</div>}
            <PhraseChips phrase={phrase} />
            {(phrase.notes ?? []).slice(0, 2).map((n, i) => <NoteView key={i} note={n} />)}
          </>
        ) : (
          <p className="faint small">Натисни, щоб перевірити себе</p>
        )}
      </div>
      {open ? (
        <GradeBar onGrade={onGrade} />
      ) : (
        <div style={{ padding: 'var(--sp-3) var(--sp-4) var(--sp-4)' }}>
          <button className="btn btn--wide" onClick={() => setOpen(true)}>Показати відповідь</button>
        </div>
      )}
    </>
  )
}

function GradeBar({ onGrade }: { onGrade: (g: Grade) => void }) {
  return (
    <div className="study__actions">
      <button className="grade grade--again" onClick={() => onGrade('again')}>Ще раз<small>забув</small></button>
      <button className="grade grade--hard" onClick={() => onGrade('hard')}>Важко<small>ледь</small></button>
      <button className="grade grade--good" onClick={() => onGrade('good')}>Добре<small>згадав</small></button>
      <button className="grade grade--easy" onClick={() => onGrade('easy')}>Легко<small>одразу</small></button>
    </div>
  )
}

// ── Вибір перекладу ───────────────────────────────────────────────────────

function Choose({
  phrase, onGrade, store,
}: { phrase: Phrase; showTr: boolean; onGrade: (g: Grade) => void; store: ReturnType<typeof useStore> }) {
  const options = useMemo(() => {
    const wrong = distractors(store.idx, phrase, 3, hash(phrase.id))
    return shuffle([phrase, ...wrong], hash(phrase.id + 'opt'))
  }, [phrase, store.idx])
  const [picked, setPicked] = useState<string | null>(null)
  const correct = picked === phrase.id

  return (
    <>
      <div className="study__body">
        <span className="chip">що це означає?</span>
        <div className="study__pl">{phrase.pl}</div>
        <Speak audio={phrase.audio} text={phrase.pl} />
        <div className="stack" style={{ width: '100%', marginTop: 8 }}>
          {options.map((o) => (
            <button
              key={o.id}
              className={`option${picked ? (o.id === phrase.id ? ' is-correct' : o.id === picked ? ' is-wrong' : ' is-muted') : ''}`}
              disabled={!!picked}
              onClick={() => setPicked(o.id)}
            >
              {o.uk}
            </button>
          ))}
        </div>
        {picked && !correct && (
          <div className="feedback feedback--no">Правильно: {phrase.uk}</div>
        )}
        {picked && (phrase.notes ?? []).slice(0, 1).map((n, i) => <NoteView key={i} note={n} />)}
      </div>
      <div style={{ padding: 'var(--sp-3) var(--sp-4) var(--sp-4)' }}>
        <button
          className="btn btn--primary btn--wide"
          disabled={!picked}
          onClick={() => onGrade(correct ? 'good' : 'again')}
        >
          Далі
        </button>
      </div>
    </>
  )
}

// ── Диктант: почув — обери, що це було ────────────────────────────────────

function Listen({
  phrase, showTr, onGrade, store,
}: { phrase: Phrase; showTr: boolean; onGrade: (g: Grade) => void; store: ReturnType<typeof useStore> }) {
  const options = useMemo(() => {
    const wrong = distractors(store.idx, phrase, 3, hash(phrase.id + 'l'))
    return shuffle([phrase, ...wrong], hash(phrase.id + 'lopt'))
  }, [phrase, store.idx])
  const [picked, setPicked] = useState<string | null>(null)
  const correct = picked === phrase.id

  return (
    <>
      <div className="study__body">
        <span className="chip">на слух</span>
        <Speak audio={phrase.audio} text={phrase.pl} auto />
        <p className="tiny faint">Натисни ще раз, щоб повторити. Утримуй — почуєш повільно.</p>
        <div className="stack" style={{ width: '100%', marginTop: 8 }}>
          {options.map((o) => (
            <button
              key={o.id}
              className={`option${picked ? (o.id === phrase.id ? ' is-correct' : o.id === picked ? ' is-wrong' : ' is-muted') : ''}`}
              disabled={!!picked}
              onClick={() => setPicked(o.id)}
            >
              {o.pl}
              <div className="option__uk">{o.uk}</div>
            </button>
          ))}
        </div>
        {picked && showTr && phrase.tr && <div className="study__tr">{phrase.tr}</div>}
      </div>
      <div style={{ padding: 'var(--sp-3) var(--sp-4) var(--sp-4)' }}>
        <button
          className="btn btn--primary btn--wide"
          disabled={!picked}
          onClick={() => onGrade(correct ? 'good' : 'again')}
        >
          Далі
        </button>
      </div>
    </>
  )
}

// ── Збери фразу з плиток ──────────────────────────────────────────────────

function Build({ phrase, onGrade }: { phrase: Phrase; showTr: boolean; onGrade: (g: Grade) => void }) {
  const words = useMemo(() => tokenize(phrase.pl), [phrase.pl])
  const pool = useMemo(() => shuffle(words.map((w, i) => ({ w, i })), hash(phrase.id + 'b')), [words, phrase.id])
  const [chosen, setChosen] = useState<number[]>([])
  const [checked, setChecked] = useState(false)

  const built = chosen.map((i) => words[i]!).join(' ')
  const correct = built === phrase.pl.replace(/\s+/g, ' ').trim()

  // Однослівні фрази збирати нема з чого — тоді це просто перевірка вимови.
  if (words.length < 2) return <Flip phrase={phrase} showTr onGrade={onGrade} />

  return (
    <>
      <div className="study__body">
        <span className="chip">збери фразу</span>
        <div className="study__uk" style={{ color: 'var(--text)' }}>{phrase.uk}</div>
        <div className="answer-slot">
          {chosen.length === 0 && <span className="faint small">Обирай слова по порядку</span>}
          {chosen.map((i, k) => (
            <button key={k} className="token" onClick={() => !checked && setChosen((c) => c.filter((_, j) => j !== k))}>
              {words[i]}
            </button>
          ))}
        </div>
        <div className="tokens">
          {pool.map(({ w, i }) => (
            <button
              key={i}
              className={`token${chosen.includes(i) ? ' is-used' : ''}`}
              onClick={() => setChosen((c) => [...c, i])}
            >
              {w}
            </button>
          ))}
        </div>
        {checked && (
          <>
            <div className={`feedback feedback--${correct ? 'ok' : 'no'}`}>
              {correct ? 'Точно так' : `Правильно: ${phrase.pl}`}
            </div>
            <Speak audio={phrase.audio} text={phrase.pl} auto />
          </>
        )}
      </div>
      <div style={{ padding: 'var(--sp-3) var(--sp-4) var(--sp-4)' }}>
        {checked ? (
          <button className="btn btn--primary btn--wide" onClick={() => onGrade(correct ? 'good' : 'again')}>Далі</button>
        ) : (
          <button className="btn btn--wide" disabled={chosen.length !== words.length} onClick={() => setChecked(true)}>
            Перевірити
          </button>
        )}
      </div>
    </>
  )
}

// ── Введення з клавіатури ─────────────────────────────────────────────────

function Type({ phrase, showTr, onGrade }: { phrase: Phrase; showTr: boolean; onGrade: (g: Grade) => void }) {
  const [value, setValue] = useState('')
  const [result, setResult] = useState<ReturnType<typeof matchAnswer> | null>(null)
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => { ref.current?.focus() }, [])

  const check = () => setResult(matchAnswer(value, [phrase.pl]))

  return (
    <>
      <div className="study__body">
        <span className="chip">скажи польською</span>
        <div className="study__uk" style={{ color: 'var(--text)' }}>{phrase.uk}</div>
        <input
          ref={ref}
          className="input"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && !result && value.trim() && check()}
          placeholder="Польською…"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          disabled={!!result}
        />
        <p className="tiny faint">Польські знаки не обовʼязкові — «mowie» зарахується як «mówię».</p>
        {result && (
          <>
            <div className={`feedback feedback--${result.correct ? 'ok' : 'no'}`}>
              {result.correct ? (result.hint ?? 'Правильно') : `Правильно: ${phrase.pl}`}
            </div>
            {showTr && phrase.tr && <div className="study__tr">{phrase.tr}</div>}
            <Speak audio={phrase.audio} text={phrase.pl} auto />
          </>
        )}
      </div>
      <div style={{ padding: 'var(--sp-3) var(--sp-4) var(--sp-4)' }}>
        {result ? (
          <button
            className="btn btn--primary btn--wide"
            onClick={() => onGrade(result.quality === 'exact' ? 'good' : result.correct ? 'hard' : 'again')}
          >
            Далі
          </button>
        ) : (
          <div className="stack--tight stack">
            <button className="btn btn--primary btn--wide" disabled={!value.trim()} onClick={check}>Перевірити</button>
            <button className="btn btn--ghost btn--wide" onClick={() => { setValue(phrase.pl); setResult(matchAnswer(phrase.pl, [phrase.pl])) }}>
              Не пригадую
            </button>
          </div>
        )}
      </div>
    </>
  )
}

// ── Підсумок ──────────────────────────────────────────────────────────────

function Finished({ done, mode }: { done: number; mode: string }) {
  return (
    <div className="screen">
      <div className="card center stack" style={{ marginTop: 40 }}>
        <div style={{ fontSize: 52 }}>🎉</div>
        <h2>{done > 0 ? 'Заняття завершено' : 'Тут поки нема чого повторювати'}</h2>
        {done > 0 && <p className="muted">Пройдено карток: {done}</p>}
        <button className="btn btn--primary btn--wide" onClick={() => navigate('/')}>На головну</button>
        {mode === 'daily' && (
          <button className="btn btn--ghost btn--wide" onClick={() => navigate('/modules')}>Відкрити модулі</button>
        )}
      </div>
    </div>
  )
}

function hash(s: string): number {
  let h = 2166136261
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619) }
  return h >>> 0
}
